import { BASE_URL } from '@/config/api';

export interface ShopTicket {
  id: string;
  code: string;
  used: boolean;
  usedAt: string | null;
  eventTitle: string | null;
}

export interface ShopOrder {
  id: string;
  reference: string;
  productTitle: string;
  productType: 'tickets' | 'pass';
  date: string | null;
  total: number;
  currency: string;
  status: string;
  invoiceId: number | null;
  ticketsTotal: number;
  ticketsUsed: number;
  amountUsed: number;
  tickets: ShopTicket[];
}

const asArray = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  for (const key of ['data', 'orders', 'purchases', 'results', 'items', 'tickets']) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return [];
};

const normalizeTicket = (raw: any, index: number): ShopTicket => {
  const used =
    raw?.used === true ||
    raw?.is_used === true ||
    raw?.used === 1 ||
    raw?.is_used === 1 ||
    Boolean(raw?.used_at ?? raw?.usedAt);
  return {
    id: String(raw?.id ?? raw?.ticket_id ?? `${index}`),
    code: String(raw?.code ?? raw?.reference ?? raw?.number ?? `#${index + 1}`),
    used,
    usedAt: raw?.used_at ?? raw?.usedAt ?? null,
    eventTitle: raw?.event_title ?? raw?.eventTitle ?? raw?.event?.title ?? null,
  };
};

const normalizeOrder = (raw: any, index: number): ShopOrder => {
  const tickets = asArray(raw?.tickets ?? raw?.coupons ?? []).map(normalizeTicket);
  const ticketsTotal = Number(
    raw?.tickets_total ?? raw?.ticketsTotal ?? raw?.quantity ?? tickets.length ?? 0
  );
  const ticketsUsed = Number(
    raw?.tickets_used ?? raw?.ticketsUsed ?? tickets.filter((t) => t.used).length ?? 0
  );
  const rawType = String(
    raw?.product_type ?? raw?.productType ?? raw?.product?.type ?? raw?.type ?? 'tickets'
  ).toLowerCase();
  return {
    id: String(raw?.id ?? raw?.order_id ?? index),
    reference: String(raw?.reference ?? raw?.number ?? raw?.id ?? index),
    productTitle:
      raw?.product_title ?? raw?.productTitle ?? raw?.product?.title ?? raw?.title ?? '—',
    productType: rawType === 'pass' ? 'pass' : 'tickets',
    date: raw?.created_at ?? raw?.createdAt ?? raw?.date ?? null,
    total: Number(raw?.total ?? raw?.amount ?? raw?.total_price ?? 0),
    currency: raw?.currency ?? 'EUR',
    status: String(raw?.status ?? 'paid'),
    invoiceId: raw?.invoice_id ?? raw?.invoiceId ?? null,
    ticketsTotal,
    ticketsUsed,
    amountUsed: Number(raw?.amount_used ?? raw?.amountUsed ?? raw?.used_amount ?? raw?.used_price ?? 0),
    tickets,
  };
};

export const getShopOrders = async (token: string | null): Promise<ShopOrder[]> => {
  const response = await fetch(`${BASE_URL}/api/shop/orders`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch orders');
  }
  const data = await response.json();
  return asArray(data).map(normalizeOrder);
};

export const getShopOrder = async (
  token: string | null,
  orderId: string
): Promise<ShopOrder | null> => {
  const response = await fetch(`${BASE_URL}/api/shop/orders/${orderId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch order');
  }
  const data = await response.json();
  const raw = data?.order ?? data?.data ?? data;
  if (!raw || Array.isArray(raw)) return null;
  return normalizeOrder(raw, 0);
};
